import Diagram, { type RenderToWhiteboardOptions } from '../../common/Diagram';
import { randomId } from '../../common/utils';
import dagre from '@dagrejs/dagre';
import graphlib from '@dagrejs/graphlib';
import { type WhiteboardData, type LayoutOptions } from '../../types';
import {
  whiteboardObjectForNode,
  whiteboardConnectorForEdge,
} from '../../common/whiteboardUtils';
import properties from './properties';
import description from './description';

class FlowchartDiagram extends Diagram {
  static diagramName = 'Flowchart';
  static diagramType = 'flowchart';

  static description = description;
  static diagramProperties = properties;
  static allowedProperties = {
    ...Diagram.allowedProperties,
    ...this.diagramProperties,
  };

  detect(): boolean {
    throw new Error('Method not implemented.');
  }

  graphProperties(): any {
    const orientation = this.getDiagramProperty('orientation') as string;
    const rankGap = this.getDiagramProperty('rankGap') as number;
    const nodeGap = this.getDiagramProperty('nodeGap') as number;

    let rankdir = 'TB';
    switch (orientation) {
      case 'south':
        rankdir = 'TB';
        break;
      case 'north':
        rankdir = 'BT';
        break;
      case 'east':
        rankdir = 'LR';
        break;
      case 'west':
        rankdir = 'RL';
        break;
    }

    return {
      rankdir,
      ranksep: rankGap,
      nodesep: nodeGap,
    };
  }

  async layout(layoutOptions: LayoutOptions) {
    // Populate a dagre graph with the nodes and edges so dagre can do the layout.
    const graph = new graphlib.Graph();
    for (const nodeId in this.database.nodesById) {
      const node = this.database.node(nodeId);

      graph.setNode(nodeId, { id: nodeId });
      const graphNode = graph.node(nodeId);
      await this.sizeNode(node, layoutOptions);
      graphNode.width = node.layout.width;
      graphNode.height = node.layout.height;
    }
    for (const edgeId in this.database.edgesById) {
      const edge = this.database.edge(edgeId);
      graph.setEdge(edge.from, edge.to, { id: edgeId });
    }

    // Automatically determine the shape of each node based on the connectors.
    if (this.getDiagramProperty('autoShape') === 'true') {
      for (const nodeId in this.database.nodesById) {
        const node = this.database.node(nodeId);
        let countIncomingEdges = 0;
        let countOutgoingEdges = 0;

        for (const edgeId of node.edges) {
          const edge = this.database.edge(edgeId);

          if (edge.from === nodeId) countOutgoingEdges++;
          if (edge.to === nodeId) countIncomingEdges++;
        }

        if (countOutgoingEdges === 2) {
          node.layout.shape = 'diamond';
          // Make diamonds shapes squarer.
          node.layout.height = Math.max(
            node.layout.height,
            node.layout.width * 0.8
          );
          graph.node(nodeId).height = node.layout.height;
        } else if (countIncomingEdges === 0 && countOutgoingEdges === 1) {
          node.layout.shape = 'processEnd';
        } else if (countIncomingEdges === 1 && countOutgoingEdges === 0) {
          node.layout.shape = 'processEnd';
        }
      }
    }

    // Layout the graph.
    graph.setGraph(this.graphProperties());
    // @ts-expect-error - Type compatibility issue with Graph
    dagre.layout(graph);

    // Copy the layout information back to the nodes.
    this.database.layout.width = graph.graph().width;
    this.database.layout.height = graph.graph().height;

    for (const nodeId of graph.nodes()) {
      const graphNode = graph.node(nodeId);
      const node = this.database.node(nodeId);

      node.layout.left = graphNode.x - graphNode.width / 2;
      node.layout.top = graphNode.y - graphNode.height / 2;
    }

    for (const graphEdge of graph.edges()) {
      const edgeData = graph.edge(graphEdge.v, graphEdge.w);
      const edgeId = edgeData.id;
      const edge = this.database.edge(edgeId);

      edge.layout.points = edgeData.points;
    }

    // Measure any captions.
    if (layoutOptions.measureTextFunc) {
      for (const edge of this.database.edges()) {
        if (edge.caption) {
          const dimensions = await layoutOptions.measureTextFunc(edge.caption, {
            shapeName: 'text',
          });
          edge.layout.captionWidth = dimensions.width;
        }
      }
    }
  }

  renderToWhiteboard(options?: RenderToWhiteboardOptions): any {
    const { nodeAttrs, edgeAttrs } = options || {};
    const whiteboard: WhiteboardData = {
      version: '1.2.6',
      objects: {},
    };

    for (const id in this.database.nodesById) {
      const node = this.database.node(id);
      whiteboardObjectForNode(whiteboard, this, node, {}, nodeAttrs?.(node));
    }

    for (const id in this.database.edgesById) {
      const edge = this.database.edge(id);
      const connectorProps = {};
      if (edgeAttrs) {
        const node = this.database.node(edge.from);
        const child = this.database.node(edge.to);
        Object.assign(connectorProps, edgeAttrs(node, child));
      }
      whiteboard.objects[id] = whiteboardConnectorForEdge(
        this,
        edge,
        connectorProps
      );

      if (edge.caption) {
        const colorScheme = this.getColorScheme();
        const connectorColor = { ...colorScheme.connectorColor({}) };

        const labelId = randomId();
        whiteboard.objects[labelId] = {
          type: 'shape',
          id: labelId,
          shapeName: 'text',
          textContent: `<p><span style="color:${connectorColor.text}">${edge.caption}</span></p>`,
          height: 30,
          width: edge.layout.captionWidth,
          scaleX: 1,
          scaleY: 1,
          fontSize: 14,
          connectorId: edge.id,
          labelOrientation: 'inline',
        };
      }
    }

    return whiteboard;
  }
}

export default FlowchartDiagram;
